<template>
    <div class="p-4 bg-white rounded shadow-sm">
        <div class="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
            <div>
                <b-button variant="info" @click="prepareAddSection()">
                    <i class="fas fa-plus-square mr-1" style="vertical-align: middle; font-size: 1rem;"></i>
                    <span style="vertical-align: middle;">Add Section</span>
                </b-button>
                <b-button variant="warning" class="ml-2" @click="openMergeModal()">
                    <i class="fas fa-compress-alt mr-1" style="vertical-align: middle; font-size: 1rem;"></i>
                    <span style="vertical-align: middle;">Merge Sections</span>
                </b-button>
            </div>
        </div>

       <div v-if="errorMsg" class="position-relative border border-danger rounded p-2 mb-2" style="max-width: 400px;">
            <span @click="errorMsg = null"
                    style="position: absolute; top: 4px; right: 8px; cursor: pointer; font-size: 1.2rem;">
                &times;
            </span>
            <span class="text-danger d-block">
                <strong>{{ errorMsg }}</strong>
            </span>
        </div>

        <br>
        <table class="table table-striped table-bordered">
            <thead class="thead-dark text-left">
                <tr>
                    <th>Order No</th>
                    <th>Section Name</th>
                    <th>Section Type</th>
                    <th>Category</th>
                    <th>Payment Component</th>
                    <th>Questions per Page</th>
                    <th>Questions</th>
                    <th>Manage Verifier</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(section, sindex) in admissionformSections" :key="sindex">
                    <td>{{ section.order_no }}</td>
                    <td>{{ section.name }}</td>
                    <td>
                        <span v-if="section.type == 'form'"> Form </span>
                        <span v-else-if="section.type == 'payment_collection'"> Payment Collection </span>
                        <span v-else-if="section.type == 'manual_verification'" v-b-tooltip.hover
                            title="Not shown to applicants or approvers. Printed blank in the PDF for staff to fill in."> Offline Verification </span>
                    </td>
                    <td>
                        <a v-if="section.type == 'payment_collection'" @click="openCategoriesModal(section)" class="text-primary text-decoration-none" style="cursor: pointer;">
                            {{ section.payment_categories_count || 0 }} {{ section.payment_categories_count === 1 ? 'Category' : 'Categories' }}
                            <i
                                class="fas fa-info-circle text-info"
                                v-b-tooltip.hover
                                title="Add a category only if the fee amount is different for different student categories (e.g. General, OBC, SC/ST). If every student pays the same fee, you don't need to create a category."
                            ></i>
                        </a>
                    </td>
                    <td>
                        <a v-if="section.type == 'payment_collection'" @click="openComponentsModal(section)" class="text-primary text-decoration-none" style="cursor: pointer;">
                            {{ section.payment_components_count || 0 }} {{ section.payment_components_count === 1 ? 'Component' : 'Components' }}
                        </a>
                    </td>
                    <td>
                        <a v-if="section.type == 'form'" @click="openQuestionsLimitModal(section)" class="text-primary text-decoration-none" style="cursor: pointer;">
                            {{ section.questions_per_page || 'All' }}
                        </a>
                    </td>
                    <td>
                        <a
                            v-if="section.type != 'payment_collection'"
                            @click="createAdmissionFormQuestions(section.id)"
                            class="text-primary font-weight-bold text-decoration-none"
                            style="cursor: pointer;">
                            Create Questions
                        </a>
                    </td>
                    <td>
                        <template v-if="section.type == 'form'">
                            <i @click="$bvModal.show('manageApprovers' + sindex)" class="fas fa-cog text-info ml-2" style="font-size: 15px!important;"></i>
                            <b-modal
                                :id="'manageApprovers' + sindex"
                                :title="'Add/Remove Approvers: ' + section.name"
                                no-close-on-backdrop
                                no-close-on-keyboard
                                no-close-on-esc
                                hide-footer
                                size="lg"
                              >
                                <Approvers :sectionId="section.id" />
                              </b-modal>
                        </template>
                    </td>
                    <td>
                        <i class="fas fa-edit text-info ml-2" style="font-size: 15px!important;cursor: pointer;"
                            @click="editAdmissionFormSection(section)"></i>
                        <i class="fas fa-trash text-danger ml-2" style="font-size: 15px!important;cursor: pointer;"
                            @click="deleteAdmissionFormSection(section)"></i>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Create Questions Modal -->
        <dashboard-modal
            id="create-questions-modal"
            title="Create Questions"
            icon="fas fa-question-circle"
            parent="Create Form"
            @close="closeQuestionsModal"
        >
            <admission-form-questions
                v-if="showQuestionsModal"
                :initial-form-id="admissionform_id"
                :initial-section-id="selectedSectionId"
            />
        </dashboard-modal>

        <!-- Add Section Modal -->
        <b-modal ref="addSectionModal" title="Add Section" no-close-on-backdrop no-close-on-keyboard hide-footer>
            <b-form @submit.prevent="submitSection">
                <!-- Order No -->
                <b-form-group label-cols="12">
                    <label>
                        Order No <span class="text-danger">*</span>
                    </label>
                    <b-form-input id="order-no" type="number" v-model="section.order_no"
                        :class="{ 'form-group-error': $v.section.order_no.$error }"
                        @blur="$v.section.order_no.$touch()" />
                    <span class="input-error-message"
                        v-if="!$v.section.order_no.required && $v.section.order_no.$dirty">
                        Order No is required!
                    </span>
                </b-form-group>

                <!-- Section Name -->
                <b-form-group label-cols="12">
                    <label>
                        Section Name <span class="text-danger">*</span>
                    </label>
                    <b-form-input id="section-name" v-model="section.name"
                        :class="{ 'form-group-error': $v.section.name.$error }" @blur="$v.section.name.$touch()" />
                    <span class="input-error-message" v-if="!$v.section.name.required && $v.section.name.$dirty">
                        Section Name is required!
                    </span>
                </b-form-group>

                <!-- Section Type -->
                <b-form-group label-cols="12">
                    <label>
                        Section Type <span class="text-danger">*</span>
                    </label>
                    <b-form-select id="section-type" v-model="section.type" :options="sectionTypes"
                        :class="{ 'form-group-error': $v.section.type.$error }" @blur="$v.section.type.$touch()" />
                    <span class="input-error-message" v-if="!$v.section.type.required && $v.section.type.$dirty">
                        Section Type is required!
                    </span>
                </b-form-group>

                <!-- Submit and Cancel Buttons -->
                <div class="text-right">
                    <b-button variant="primary" type="submit">Submit</b-button>
                    <b-button variant="danger" class="ml-2" @click="$refs.addSectionModal.hide()">Cancel</b-button>
                </div>
            </b-form>
        </b-modal>

        <!-- Merge Sections Modal -->
        <b-modal ref="mergeSectionsModal" title="Merge Sections" no-close-on-backdrop no-close-on-keyboard hide-footer>
            <b-form @submit.prevent="submitMerge">
                <b-form-group label-cols="12">
                    <label>Target Section <span class="text-danger">*</span></label>
                    <small class="d-block text-muted mb-1">All source content will be merged into this section.</small>
                    <b-form-select
                        v-model="merge.targetSectionId"
                        :options="mergeSectionOptions"
                        value-field="id"
                        text-field="label"
                    >
                        <template #first>
                            <b-form-select-option :value="null" disabled>-- Select Target section --</b-form-select-option>
                        </template>
                    </b-form-select>
                </b-form-group>

                <b-form-group v-if="merge.targetSectionId" label-cols="12">
                    <label>Source Sections <span class="text-danger">*</span></label>
                    <small class="d-block text-muted mb-1">Select one or more sections to merge into the destination.</small>
                    <div v-for="section in mergeSourceOptions" :key="section.id" class="mb-1">
                        <b-form-checkbox
                            :value="section.id"
                            v-model="merge.sourceSectionIds"
                        >
                            {{ section.label }}
                        </b-form-checkbox>
                    </div>
                </b-form-group>

                <div class="text-right">
                    <b-button variant="warning" type="submit" :disabled="mergeSubmitting || !merge.targetSectionId || merge.sourceSectionIds.length === 0">
                        <span v-if="mergeSubmitting">Merging...</span>
                        <span v-else>Merge</span>
                    </b-button>
                    <b-button variant="danger" class="ml-2" @click="$refs.mergeSectionsModal.hide()">Cancel</b-button>
                </div>
            </b-form>
        </b-modal>

        <!-- Delete Section Confirmation Modal -->
        <b-modal ref="deleteSectionModal" title="Delete Section" no-close-on-backdrop no-close-on-keyboard no-close-on-esc hide-footer>
            <p class="text-danger font-weight-bold">
                Warning: Deleting this section will permanently remove all questions and titles.
            </p>
            <p>Type <strong>DELETE</strong> below to confirm:</p>
            <b-form-input v-model="deleteConfirmText" placeholder='Type "DELETE" to confirm' />
            <div class="text-right mt-3">
                <b-button variant="danger" :disabled="deleteConfirmText !== 'DELETE'" @click="confirmDeleteSection">Delete</b-button>
                <b-button variant="secondary" class="ml-2" @click="$refs.deleteSectionModal.hide()">Cancel</b-button>
            </div>
        </b-modal>

        <!-- Questions Limit Modal -->
        <b-modal ref="questionsLimitModal" title="Set Questions per Page" no-close-on-backdrop no-close-on-keyboard hide-footer>
            <b-form @submit.prevent="submitQuestionsLimit">
                <b-form-group label-cols="12">
                    <label>Questions per Page</label>
                    <b-form-input
                        type="number"
                        min="1"
                        v-model.number="questionsLimit.value"
                        placeholder="Leave empty to show all questions on one page"
                    />
                    <small class="text-muted">Leave empty to show all questions in a single page.</small>
                </b-form-group>
                <div class="text-right">
                    <b-button variant="primary" type="submit">Update</b-button>
                    <b-button variant="danger" class="ml-2" @click="$refs.questionsLimitModal.hide()">Cancel</b-button>
                </div>
            </b-form>
        </b-modal>

        <!-- Payment Categories Modal: lets admin add/remove the fee categories (e.g. General, OBC, SC/ST) for a payment_collection section. Only needed when the fee amount differs by category. -->
        <b-modal ref="categoriesModal" :title="'Manage Categories: ' + (categoriesModal.section ? categoriesModal.section.name : '')" no-close-on-backdrop no-close-on-keyboard hide-footer>
            <div v-if="categoriesModal.error" class="alert alert-danger py-2">{{ categoriesModal.error }}</div>

            <div class="mb-3">
                <b-button v-if="!categoriesModal.showAddForm" variant="info" size="sm" @click="openAddCategoryForm()">
                    Add Category
                </b-button>

                <b-form v-else @submit.prevent="submitCategory">
                    <b-form-group label-cols="12">
                        <label>
                            Name <span class="text-danger">*</span>
                        </label>
                        <b-form-input v-model="categoriesModal.form.name" />
                        <span class="input-error-message" v-if="categoriesModal.fieldErrors.name">
                            {{ categoriesModal.fieldErrors.name }}
                        </span>
                    </b-form-group>
                    <div class="text-right">
                        <b-button variant="primary" type="submit">Save</b-button>
                        <b-button variant="danger" class="ml-2" @click="closeAddCategoryForm()">Cancel</b-button>
                    </div>
                </b-form>
            </div>

            <template v-if="!categoriesModal.showAddForm">
                <table class="table table-striped table-bordered" v-if="categoriesModal.list.length">
                    <thead class="thead-dark text-left">
                        <tr>
                            <th>Name</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="cat in categoriesModal.list" :key="cat.id">
                            <td>
                                {{ cat.name }}
                                <!-- "Mapped" = a payment component already points to this category, so it can't be deleted yet -->
                                <span v-if="cat.components_count && !cat.payments_count" class="badge badge-secondary ml-2">Mapped</span>
                            </td>
                            <td>
                                <!-- Once a student has paid using this category, lock it instead of showing edit/delete -->
                                <i
                                    v-if="cat.payments_count"
                                    class="fas fa-lock text-warning ml-2"
                                    style="cursor: pointer;"
                                    v-b-tooltip.hover
                                    title="Payment done"
                                ></i>
                                <!-- Mapped to a component: same rule as delete - edit is blocked too -->
                                <template v-else-if="!cat.components_count">
                                    <i class="fas fa-edit text-info ml-2" style="font-size: 15px!important;cursor: pointer;" @click="editCategory(cat)"></i>
                                    <i class="fas fa-trash text-danger ml-2" style="font-size: 15px!important;cursor: pointer;" @click="deleteCategory(cat)"></i>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-else class="text-muted">No categories added yet.</p>
            </template>
        </b-modal>

        <!-- Payment Components Modal: the actual fee items for a payment_collection section (e.g. Admission Fee, Exam Fee), each with its own amount and, optionally, a category. -->
        <b-modal ref="componentsModal" :title="'Manage Payment Components: ' + (componentsModal.section ? componentsModal.section.name : '')" no-close-on-backdrop no-close-on-keyboard hide-footer size="lg">
            <div v-if="componentsModal.error" class="alert alert-danger py-2">{{ componentsModal.error }}</div>

            <div class="mb-3">
                <b-button v-if="!componentsModal.showAddForm" variant="info" size="sm" @click="openAddComponentForm()">
                    Add Payment Component
                </b-button>

                <b-form v-else @submit.prevent="submitComponent">
                    <b-form-group label-cols="12">
                        <label>
                            Name <span class="text-danger">*</span>
                        </label>
                        <b-form-input v-model="componentsModal.form.name" />
                        <span class="input-error-message" v-if="componentsModal.fieldErrors.name">
                            {{ componentsModal.fieldErrors.name }}
                        </span>
                    </b-form-group>
                    <b-form-group label-cols="12">
                        <label>Description</label>
                        <b-form-textarea v-model="componentsModal.form.description" rows="2" />
                    </b-form-group>
                    <b-form-group label-cols="12">
                        <label>
                            Category
                            <span v-if="componentsModal.categories.length" class="text-danger">*</span>
                        </label>
                        <b-form-select v-model="componentsModal.form.payment_category_id" :options="componentsModal.categories" value-field="id" text-field="name">
                            <template #first>
                                <b-form-select-option :value="null">
                                    -- {{ componentsModal.categories.length ? 'Select Category' : 'No categories added yet' }} --
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                        <span class="input-error-message" v-if="componentsModal.fieldErrors.payment_category_id">
                            {{ componentsModal.fieldErrors.payment_category_id }}
                        </span>
                    </b-form-group>
                    <b-form-group label-cols="12">
                        <label>
                            Amount <span class="text-danger">*</span>
                        </label>
                        <b-form-input type="number" min="0" step="0.01" v-model="componentsModal.form.amount" />
                        <span class="input-error-message" v-if="componentsModal.fieldErrors.amount">
                            {{ componentsModal.fieldErrors.amount }}
                        </span>
                    </b-form-group>
                    <div class="text-right">
                        <b-button variant="primary" type="submit">Save</b-button>
                        <b-button variant="danger" class="ml-2" @click="closeAddComponentForm()">Cancel</b-button>
                    </div>
                </b-form>
            </div>

            <template v-if="!componentsModal.showAddForm">
                <table class="table table-striped table-bordered" v-if="componentsModal.list.length">
                    <thead class="thead-dark text-left">
                        <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Category</th>
                            <th>Amount</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="comp in componentsModal.list" :key="comp.id">
                            <td>{{ comp.name }}</td>
                            <td>{{ comp.description || '-' }}</td>
                            <td>{{ comp.payment_category ? comp.payment_category.name : '-' }}</td>
                            <td>{{ comp.amount }}</td>
                            <td>
                                <!-- Same idea as categories: lock instead of allowing edit/delete once a payment has used this component's amount -->
                                <i
                                    v-if="comp.is_locked"
                                    class="fas fa-lock text-warning ml-2"
                                    style="cursor: pointer;"
                                    v-b-tooltip.hover
                                    title="Payment done"
                                ></i>
                                <template v-else>
                                    <i class="fas fa-edit text-info ml-2" style="font-size: 15px!important;cursor: pointer;" @click="editComponent(comp)"></i>
                                    <i class="fas fa-trash text-danger ml-2" style="font-size: 15px!important;cursor: pointer;" @click="deleteComponent(comp)"></i>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-else class="text-muted">No payment components added yet.</p>
            </template>
        </b-modal>

    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import Approvers from "./admissionFormApprovers.vue";
import DashboardModal from '../DashboardModal.vue';
import AdmissionFormQuestions from './admissionFormQuestions.vue';

export default {
    name: 'AdmissionFormDashboard',
    props: {
        initialFormId: { default: null },
    },
    data() {
        return {
            errorMsg: '',
            admissionform_id: null,
            admissionformSections: [],
            editMode: false,
            editingSectionId: null,
            section: {
                order_no: '',
                name: '',
                type: 'form',
            },
            sectionTypes: [
                { value: 'form', text: 'Form' },
                { value: 'payment_collection', text: 'Payment Collection' },
                { value: 'manual_verification', text: 'Offline Verification' },
            ],
            selectedSectionId: null,
            showQuestionsModal: false,
            questionsLimit: {
                sectionId: null,
                value: null,
            },
            merge: {
                targetSectionId: null,
                sourceSectionIds: [],
            },
            mergeSubmitting: false,
            sectionToDelete: null,
            deleteConfirmText: '',
            // State for the "Manage Categories" modal on a payment_collection section
            categoriesModal: {
                section: null,
                list: [],
                form: { id: null, name: '' },
                error: '',
                fieldErrors: { name: '' },
                showAddForm: false,
            },
            // State for the "Manage Payment Components" modal on a payment_collection section
            componentsModal: {
                section: null,
                list: [],
                categories: [],
                form: { id: null, name: '', description: '', payment_category_id: null, amount: '' },
                error: '',
                fieldErrors: { name: '', amount: '', payment_category_id: '' },
                showAddForm: false,
            },
        };
    },
    components: {
       Approvers, DashboardModal, AdmissionFormQuestions
    },
    validations: {
        section: {
            order_no: { required },
            name: { required },
            type: { required },
        },
    },
    computed: {
        mergeSectionOptions() {
            return this.admissionformSections.map(s => ({
                id: s.id,
                label: `${s.order_no} - ${s.name}`,
            }));
        },
        mergeSourceOptions() {
            return this.admissionformSections
                .filter(s => s.id !== this.merge.targetSectionId)
                .map(s => ({
                    id: s.id,
                    label: `${s.order_no} - ${s.name}`,
                }));
        },
    },
    watch: {
        'merge.targetSectionId'(newVal) {
            this.merge.sourceSectionIds = this.merge.sourceSectionIds.filter(id => id !== newVal);
        },
    },
    async created() {
        this.admissionform_id = this.initialFormId || this.$route.params.admissionform_id;
        await this.fetchSections();
    },
    methods: {
        createAdmissionFormQuestions(admissionformSectionId) {
            this.selectedSectionId = admissionformSectionId;
            this.showQuestionsModal = true;
            this.$bvModal.show('create-questions-modal');
        },
        closeQuestionsModal() {
            this.$bvModal.hide('create-questions-modal');
            this.showQuestionsModal = false;
            this.selectedSectionId = null;
        },
        prepareAddSection() {
            this.editMode = false;
            this.editingSectionId = null;
            this.section = { order_no: '', name: '', type: 'form' };
            this.$v.$reset();
            this.$refs.addSectionModal.show();
        },
        editAdmissionFormSection(section) {
            this.editMode = true;
            this.editingSectionId = section.id;
            this.section = {
                order_no: section.order_no,
                name: section.name,
                type: section.type,
            };
            this.$refs.addSectionModal.show();
        },
        deleteAdmissionFormSection(section) {
            this.sectionToDelete = section;
            this.deleteConfirmText = '';
            this.$refs.deleteSectionModal.show();
        },
        async confirmDeleteSection() {
            const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformsections/${this.sectionToDelete.id}`;
            try {
                await this.$axios.delete(url);
                this.$refs.deleteSectionModal.hide();
                this.$toast.success('Section deleted successfully', { position: 'top', duration: 3000 });
                await this.fetchSections();
            } catch (error) {
                console.error("Error deleting section:", error);
                this.errorMsg = error.response?.data?.message || 'An error occurred while deleting the section.';
                this.$refs.deleteSectionModal.hide();
            } finally {
                this.sectionToDelete = null;
                this.deleteConfirmText = '';
            }
        },
        async fetchSections() {
            const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformsections`;

            try {
                const res = await this.$axios.get(url, {
                    params: {
                        admissionform_id: this.admissionform_id,
                    },
                });
                this.admissionformSections = res.data;
            } catch (err) {
                console.error("Error fetching sections:", err);
            }
        },
        openQuestionsLimitModal(section) {
            this.questionsLimit = {
                sectionId: section.id,
                value: section.questions_per_page || null,
            };
            this.$refs.questionsLimitModal.show();
        },
        async submitQuestionsLimit() {
            const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformsections/${this.questionsLimit.sectionId}/questionslimit`;
            try {
                await this.$axios.patch(url, { questions_per_page: this.questionsLimit.value || null });
                this.$toast.success('Questions per page updated', { position: 'top', duration: 3000 });
                this.$refs.questionsLimitModal.hide();
                await this.fetchSections();
            } catch (error) {
                this.$toast.error('Failed to update questions per page', { position: 'top', duration: 3000 });
            }
        },
        // Opens the categories modal for a section and loads its current category list
        async openCategoriesModal(section) {
            this.categoriesModal.section = section;
            this.categoriesModal.error = '';
            this.categoriesModal.showAddForm = false;
            this.resetCategoryForm();
            await this.fetchCategories();
            this.$refs.categoriesModal.show();
        },
        async fetchCategories() {
            const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformpaymentcategories`;
            try {
                const res = await this.$axios.get(url, { params: { section_id: this.categoriesModal.section.id } });
                this.categoriesModal.list = res.data;
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        },
        resetCategoryForm() {
            this.categoriesModal.form = { id: null, name: '' };
            this.categoriesModal.fieldErrors = { name: '' };
        },
        openAddCategoryForm() {
            this.resetCategoryForm();
            this.categoriesModal.showAddForm = true;
        },
        closeAddCategoryForm() {
            this.resetCategoryForm();
            this.categoriesModal.showAddForm = false;
        },
        editCategory(cat) {
            this.categoriesModal.fieldErrors = { name: '' };
            this.categoriesModal.form = { id: cat.id, name: cat.name };
            this.categoriesModal.showAddForm = true;
        },
        async submitCategory() {
            this.categoriesModal.fieldErrors = { name: '' };
            if (!this.categoriesModal.form.name) {
                this.categoriesModal.fieldErrors.name = 'Name is required.';
                return;
            }
            // Backend also rejects same-name categories within the section
            const duplicate = this.categoriesModal.list.some((cat) =>
                cat.id !== this.categoriesModal.form.id &&
                cat.name.trim().toLowerCase() === this.categoriesModal.form.name.trim().toLowerCase()
            );
            if (duplicate) {
                this.categoriesModal.fieldErrors.name = 'A category with this name already exists.';
                return;
            }
            const mainAPI = this.$store.getters.getAPIKey.mainAPI;
            try {
                if (this.categoriesModal.form.id) {
                    await this.$axios.put(`${mainAPI}/admissionformpaymentcategories/${this.categoriesModal.form.id}`, {
                        name: this.categoriesModal.form.name,
                    }, { skipGlobalError: true });
                } else {
                    await this.$axios.post(`${mainAPI}/admissionformpaymentcategories`, {
                        section_id: this.categoriesModal.section.id,
                        name: this.categoriesModal.form.name,
                    }, { skipGlobalError: true });
                }
                this.$toast.success('Category saved successfully', { position: 'top', duration: 3000 });
                this.closeAddCategoryForm();
                await this.fetchCategories();
                await this.fetchSections();
            } catch (error) {
                this.categoriesModal.fieldErrors.name = error.response?.data?.message || 'Failed to save category.';
            }
        },
        // Backend rejects this if the category is mapped to a component or already paid
        async deleteCategory(cat) {
            const mainAPI = this.$store.getters.getAPIKey.mainAPI;
            try {
                await this.$axios.delete(`${mainAPI}/admissionformpaymentcategories/${cat.id}`, { skipGlobalError: true });
                this.$toast.success('Category deleted successfully', { position: 'top', duration: 3000 });
                await this.fetchCategories();
                await this.fetchSections();
            } catch (error) {
                this.categoriesModal.error = error.response?.data?.message || 'Failed to delete category.';
            }
        },
        // Opens the payment components modal for a section, loading both its components and
        // categories (categories are needed for the "Category" select in the add/edit form)
        async openComponentsModal(section) {
            this.componentsModal.section = section;
            this.componentsModal.error = '';
            this.componentsModal.showAddForm = false;
            this.resetComponentForm();
            await Promise.all([this.fetchComponents(), this.fetchComponentCategories()]);
            this.$refs.componentsModal.show();
        },
        async fetchComponents() {
            const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformpaymentcomponents`;
            try {
                const res = await this.$axios.get(url, { params: { section_id: this.componentsModal.section.id } });
                this.componentsModal.list = res.data;
            } catch (error) {
                console.error("Error fetching payment components:", error);
            }
        },
        async fetchComponentCategories() {
            const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformpaymentcategories`;
            try {
                const res = await this.$axios.get(url, { params: { section_id: this.componentsModal.section.id } });
                this.componentsModal.categories = res.data;
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        },
        resetComponentForm() {
            this.componentsModal.form = { id: null, name: '', description: '', payment_category_id: null, amount: '' };
            this.componentsModal.fieldErrors = { name: '', amount: '', payment_category_id: '' };
        },
        openAddComponentForm() {
            this.resetComponentForm();
            this.componentsModal.showAddForm = true;
        },
        closeAddComponentForm() {
            this.resetComponentForm();
            this.componentsModal.showAddForm = false;
        },
        editComponent(comp) {
            this.componentsModal.fieldErrors = { name: '', amount: '', payment_category_id: '' };
            this.componentsModal.form = {
                id: comp.id,
                name: comp.name,
                description: comp.description,
                payment_category_id: comp.payment_category_id,
                amount: comp.amount,
            };
            this.componentsModal.showAddForm = true;
        },
        async submitComponent() {
            this.componentsModal.fieldErrors = { name: '', amount: '', payment_category_id: '' };
            if (!this.componentsModal.form.name) {
                this.componentsModal.fieldErrors.name = 'Name is required.';
                return;
            }
            if (!this.componentsModal.form.amount) {
                this.componentsModal.fieldErrors.amount = 'Amount is required.';
                return;
            }
            const mainAPI = this.$store.getters.getAPIKey.mainAPI;
            const payload = {
                name: this.componentsModal.form.name,
                description: this.componentsModal.form.description || null,
                amount: this.componentsModal.form.amount,
                payment_category_id: this.componentsModal.form.payment_category_id || null,
            };
            try {
                if (this.componentsModal.form.id) {
                    await this.$axios.put(`${mainAPI}/admissionformpaymentcomponents/${this.componentsModal.form.id}`, payload, { skipGlobalError: true });
                } else {
                    await this.$axios.post(`${mainAPI}/admissionformpaymentcomponents`, {
                        section_id: this.componentsModal.section.id,
                        ...payload,
                    }, { skipGlobalError: true });
                }
                this.$toast.success('Payment component saved successfully', { position: 'top', duration: 3000 });
                this.closeAddComponentForm();
                await this.fetchComponents();
                await this.fetchSections();
            } catch (error) {
                // Category-mapping errors (required / already used / etc.) come back mentioning
                // "category", so route those under the Category field instead of Name
                const msg = error.response?.data?.message || 'Failed to save payment component.';
                if (/categor/i.test(msg)) {
                    this.componentsModal.fieldErrors.payment_category_id = msg;
                } else {
                    this.componentsModal.fieldErrors.name = msg;
                }
            }
        },
        // Backend rejects this once the component has already been used in a payment
        async deleteComponent(comp) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to delete the payment component "${comp.name}"?`,
                { title: 'Delete Payment Component', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel', centered: true }
            );
            if (!confirmed) return;
            const mainAPI = this.$store.getters.getAPIKey.mainAPI;
            try {
                await this.$axios.delete(`${mainAPI}/admissionformpaymentcomponents/${comp.id}`, { skipGlobalError: true });
                this.$toast.success('Payment component deleted successfully', { position: 'top', duration: 3000 });
                if (this.componentsModal.form.id === comp.id) this.resetComponentForm();
                await this.fetchComponents();
                await this.fetchSections();
            } catch (error) {
                this.componentsModal.error = error.response?.data?.message || 'Failed to delete payment component.';
            }
        },
        openMergeModal() {
            this.merge = { targetSectionId: null, sourceSectionIds: [] };
            this.$refs.mergeSectionsModal.show();
        },
        async submitMerge() {
            if (!this.merge.targetSectionId || this.merge.sourceSectionIds.length === 0) return;
            this.mergeSubmitting = true;
            try {
                const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformsections/merge`;
                await this.$axios.post(url, {
                    target_section_id: this.merge.targetSectionId,
                    source_section_ids: this.merge.sourceSectionIds,
                });
                this.$toast.success('Sections merged successfully', { position: 'top', duration: 3000 });
                this.$refs.mergeSectionsModal.hide();
                await this.fetchSections();
            } catch (error) {
                const msg = error.response?.data?.message || 'Failed to merge sections.';
                this.$toast.error(msg, { position: 'top', duration: 4000 });
            } finally {
                this.mergeSubmitting = false;
            }
        },
        closeWindow() {
            this.$emit('close');
        },
        async submitSection() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                const modalBody = this.$refs.addSectionModal.$el.querySelector('.modal-body');
                this.$smoothScroll({
                    container: modalBody,
                    scrollTo: modalBody.querySelector('.form-group-error'),
                    duration: 800,
                    offset: -60,
                });
                return;
            }

            try {
                const payload = {
                    admissionform_id: this.admissionform_id,
                    ...this.section,
                };

                if (this.editMode) {
                    const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformsections/${this.editingSectionId}`;
                    await this.$axios.put(url, payload);
                    this.$toast.success('Section updated successfully', {
                        position: 'top',
                        duration: 3000,
                    });
                } else {
                    const url = this.$store.getters.getAPIKey.mainAPI + `/admissionformsections`;
                    await this.$axios.post(url, payload);
                    this.$toast.success('Section added successfully', {
                        position: 'top',
                        duration: 3000,
                    });
                }

                // Reset
                this.section = { order_no: '', name: '', type: 'form' };
                this.$v.$reset();
                this.editMode = false;
                this.editingSectionId = null;

                this.$refs.addSectionModal.hide();
                await this.fetchSections();

            } catch (error) {
                console.error("Error saving section:", error);
                this.$toast.error(this.editMode ? 'Failed to update section' : 'Failed to add section', {
                    position: 'top',
                    duration: 3000,
                });
            }
        },
    },
};
</script>

<style scoped>
.form-group-error {
    border-color: #dc3545 !important;
}

.input-error-message {
    color: #dc3545;
    font-size: 0.875rem;
}
</style>